/**
 * Suspension Warning Dialog
 * Shown when user is suspended with auto-delete enabled
 * Shows how much storage needs to be deleted and current step in suspension process
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, X, Trash2, Clock, Download, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@config/constants';
import type { DownloadProgress } from '@services/download-all.service';
import { ModalShell } from '@components/ui/ModalShell';

interface SuspensionWarningDialogProps {
  open: boolean;
  onClose: () => void;
  storageUsed: number;
  storageLimit: number;
  daysRemaining: number;
  suspensionStartedAt: string | null;
  suspensionDownloadExpiresAt: string | null;
  suspensionGracePeriodDays: number | null;
}

export const SuspensionWarningDialog: React.FC<SuspensionWarningDialogProps> = ({
  open,
  onClose,
  storageUsed,
  storageLimit,
  daysRemaining,
  suspensionStartedAt,
  suspensionDownloadExpiresAt,
  suspensionGracePeriodDays,
}) => {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);
  const [currentDay, setCurrentDay] = useState<number>(0);
  const [totalDays, setTotalDays] = useState<number>(14);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

  // Listen for reopen event
  useEffect(() => {
    const handleReopen = () => {
      setIsDismissed(false);
    };

    window.addEventListener('suspension-warning-dialog-reopen', handleReopen);
    return () => {
      window.removeEventListener('suspension-warning-dialog-reopen', handleReopen);
    };
  }, []);

  // Calculate totalDays and currentDay based on suspension_download_expires_at
  useEffect(() => {
    if (suspensionDownloadExpiresAt && suspensionStartedAt) {
      const expiresAt = new Date(suspensionDownloadExpiresAt);
      const startedAt = new Date(suspensionStartedAt);
      
      // Calculate total days from start to expiration
      const totalDaysCalculated = Math.ceil((expiresAt.getTime() - startedAt.getTime()) / (1000 * 60 * 60 * 24));
      
      // Use grace_period_days if available, otherwise use calculated value
      const totalDaysValue = suspensionGracePeriodDays || totalDaysCalculated || 14;
      setTotalDays(totalDaysValue);
      
      // Calculate current day: totalDays - daysRemaining
      // This ensures consistency with daysRemaining (which is based on suspension_download_expires_at)
      const currentDayCalculated = Math.max(1, totalDaysValue - daysRemaining);
      setCurrentDay(currentDayCalculated);
    } else if (suspensionGracePeriodDays) {
      // Fallback: use grace_period_days if available
      setTotalDays(suspensionGracePeriodDays);
      setCurrentDay(Math.max(1, suspensionGracePeriodDays - daysRemaining));
    } else {
      // Default fallback
      setTotalDays(14);
      setCurrentDay(Math.max(1, 14 - daysRemaining));
    }
  }, [suspensionDownloadExpiresAt, suspensionStartedAt, suspensionGracePeriodDays, daysRemaining]);

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const excessStorage = storageUsed - storageLimit;
  const excessGB = (excessStorage / (1024 * 1024 * 1024)).toFixed(2);

  const handleGoToCloudExplorer = () => {
    setIsDismissed(true);
    onClose();
    navigate(ROUTES.CLOUD_EXPLORER);
  };

  const handleUpgradePlan = () => {
    setIsDismissed(true);
    onClose();
    navigate(ROUTES.BILLING);
  };

  const handleDownloadData = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress({
      stage: 'fetching',
      current: 0,
      total: 0,
      message: 'Preparing download...',
    });

    try {
      // Dynamic import to avoid bundle bloat
      const { downloadAllDataAsZip } = await import('@services/download-all.service');
      
      await downloadAllDataAsZip((progress: DownloadProgress) => {
        setDownloadProgress(progress);
      });

      // Show success message briefly before closing
      setTimeout(() => {
        setIsDismissed(true);
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Error downloading all data:', error);
      setDownloadProgress({
        stage: 'complete',
        current: 0,
        total: 0,
        message: `Error: ${error.message || 'Failed to download your data. Please try again.'}`,
      });
      // Reset after showing error
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(null);
      }, 3000);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onClose();
  };

  // Reset dismissed state when dialog is opened
  useEffect(() => {
    if (open) {
      setIsDismissed(false);
    }
  }, [open]);

  if (!open || isDismissed) {
    return null;
  }

  return (
    <ModalShell
      onClose={handleDismiss}
      open={open && !isDismissed}
      panelClassName="max-w-2xl w-full bg-white rounded-lg max-h-[90dvh] animate-slide-in-up"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Account Suspended - Deletion Pending
            </h2>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-medium">
              ⚠️ Your account has been suspended due to storage limit exceeded after a plan downgrade.
            </p>
          </div>

          {/* Storage Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Storage Used:</span>
              <span className="text-sm font-semibold text-gray-900">{formatBytes(storageUsed)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Storage Limit:</span>
              <span className="text-sm font-semibold text-gray-900">{formatBytes(storageLimit)}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-red-600">Excess Storage:</span>
              <span className="text-sm font-bold text-red-600">+{excessGB} GB</span>
            </div>
            <div className="pt-2 border-t border-gray-200 mt-2">
              <p className="text-xs text-gray-600">
                <strong>You need to delete at least {excessGB} GB</strong> to get back under your limit.
              </p>
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {daysRemaining === 0 
                    ? '⚠️ Last day - Account will be permanently deleted tomorrow'
                    : `Day ${currentDay} of ${totalDays} - ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining until deletion`}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  After {daysRemaining === 0 ? 'today' : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`}, your account and all data will be permanently deleted.
                </p>
              </div>
            </div>

            {/* Visual Timeline */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">Suspension Timeline:</h4>
              <div className="space-y-3">
                {/* Phase 1: Warning Period (Completed) */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5 bg-gray-400" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-500">
                        Phase 1: Warning Period
                      </span>
                      <span className="text-xs text-gray-400">(Completed)</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      3 days to delete files • All functions available
                    </p>
                  </div>
                </div>

                {/* Phase 2: Suspension with Download (Current) */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${daysRemaining > 0 ? 'bg-amber-600' : 'bg-gray-400'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${daysRemaining > 0 ? 'text-amber-900' : 'text-gray-500'}`}>
                        Phase 2: Account Suspended
                      </span>
                      {daysRemaining > 0 && (
                        <span className="text-xs text-amber-600 font-medium">(Current - Day {currentDay}/{totalDays})</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {daysRemaining > 0 
                        ? `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining to download your data • Uploads blocked`
                        : 'Completed - Account will be deleted'}
                    </p>
                  </div>
                </div>

                {/* Phase 3: Deletion */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${daysRemaining === 0 ? 'bg-red-600' : 'bg-red-300'}`} />
                  <div className="flex-1">
                    <span className={`text-xs font-semibold ${daysRemaining === 0 ? 'text-red-900' : 'text-gray-500'}`}>
                      Phase 3: Account Deletion
                    </span>
                    {daysRemaining === 0 && (
                      <span className="text-xs text-red-600 font-medium ml-2">(Next)</span>
                    )}
                    <p className="text-xs text-red-600 mt-0.5 font-medium">
                      Account and all data permanently deleted (no recovery possible)
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {daysRemaining > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Time remaining in Phase 2</span>
                    <span className="text-xs font-semibold text-amber-600">{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((totalDays - daysRemaining) / totalDays) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Solutions */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">What you can do:</h3>
            
            {/* Option 1: Download Data */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-blue-900">Download Your Data</h4>
              </div>
              <p className="text-xs text-blue-800">
                Download all your files as a ZIP archive before the account is permanently deleted. This is your last chance!
              </p>
              
              {/* Download Progress */}
              {downloadProgress && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-blue-700 font-medium">{downloadProgress.message}</span>
                    {downloadProgress.total > 0 && downloadProgress.stage !== 'complete' && (
                      <span className="text-blue-600">
                        {downloadProgress.current} / {downloadProgress.total}
                      </span>
                    )}
                  </div>
                  {downloadProgress.total > 0 && downloadProgress.stage !== 'complete' && (
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(downloadProgress.current / downloadProgress.total) * 100}%` }}
                      />
                    </div>
                  )}
                  {downloadProgress.stage === 'complete' && !downloadProgress.message.startsWith('Error:') && (
                    <div className="flex items-center gap-2 text-green-700 text-xs font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Download started! Check your downloads folder.
                    </div>
                  )}
                  {downloadProgress.message.startsWith('Error:') && (
                    <div className="flex items-center gap-2 text-red-700 text-xs font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {downloadProgress.message.replace('Error: ', '')}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleDownloadData}
                disabled={isDownloading}
                className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
              >
                {isDownloading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Preparing ZIP Archive...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Download All Data as ZIP</span>
                  </>
                )}
              </button>
            </div>

            {/* Option 2: Upgrade Plan */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-600" />
                <h4 className="text-sm font-semibold text-green-900">Upgrade Your Plan</h4>
              </div>
              <p className="text-xs text-green-800">
                Upgrade to a plan with more storage to prevent deletion and restore your account immediately.
              </p>
              <button
                onClick={handleUpgradePlan}
                className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
              >
                <Clock className="w-4 h-4" />
                Upgrade Plan Now
              </button>
            </div>

            {/* Option 3: Delete Files */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-purple-600" />
                <h4 className="text-sm font-semibold text-purple-900">Delete Files</h4>
              </div>
              <p className="text-xs text-purple-800">
                Delete files to reduce storage below <span className="font-semibold">{formatBytes(storageLimit)}</span>.
                Once you're under the limit, you can upgrade your plan to restore your account immediately.
              </p>
              <ol className="list-decimal list-inside space-y-1 text-xs text-purple-700 ml-4 mt-1">
                <li>Go to Cloud Explorer and delete unnecessary files</li>
                <li>Delete at least {excessGB} GB to get back under your limit</li>
                <li>Upgrade your plan to restore your account (no need to delete everything)</li>
              </ol>
            </div>
          </div>

          {/* Contact Support */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-gray-900 mb-1">Need Help?</p>
                <p className="text-xs text-gray-600">
                  For questions, contact <a href="mailto:info@fyle-cloud.com" className="text-blue-600 hover:text-blue-700 underline">info@fyle-cloud.com</a> or create a support ticket.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            I understand
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleDownloadData}
              disabled={isDownloading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
            >
              {isDownloading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Preparing...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Download Data</span>
                </>
              )}
            </button>
            <button
              onClick={handleUpgradePlan}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
            >
              <Clock className="w-4 h-4" />
              Upgrade Plan
            </button>
            <button
              onClick={handleGoToCloudExplorer}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
            >
              <Trash2 className="w-4 h-4" />
              Delete Files
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

/**
 * Function to reopen the suspension warning dialog
 * Can be called from anywhere (e.g., Header button)
 */
export const reopenSuspensionWarningDialog = () => {
  window.dispatchEvent(new CustomEvent('suspension-warning-dialog-reopen'));
};





